import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { designSystem } from "app/theme";

const NeoPanel = ({
  title,
  subtitle,
  children,
  actions,
  sx = {},
  variant = "elevated",
}) => {
  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1], // Slow, Regal
      }}
      sx={{
        width: "100%",
        borderRadius: "4px", // Sharp but slightly softened
        background: "#0A0908", // Warm Obsidian
        border: "1px solid color-mix(in srgb, var(--text-primary) 8%, transparent)", // Neutral border
        boxShadow: "0 20px 40px -10px rgba(0,0,0,0.6)", // Deep shadow
        p: { xs: 3, md: 5 },
        position: "relative",
        overflow: "hidden",
        ...sx,
      }}
    >
      {/* INNER GLOW (The Fresco) */}
      <Box
        sx={{
            position: "absolute",
            top: "-50%",
            left: "50%",
            transform: "translateX(-50%)",
            width: "120%",
            height: "120%",
            background: "radial-gradient(circle at center, rgba(99, 102, 241, 0.03) 0%, transparent 60%)",
            pointerEvents: "none",
            zIndex: 0
        }}
      />

      <Box sx={{ position: "relative", zIndex: 1 }}>
        {(title || subtitle || actions) && (
          <Box sx={{ mb: 4, display: "flex", flexDirection: "column", gap: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "flex-start", md: "center" },
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box>
                {title && (
                  <Typography
                    variant="h4"
                    sx={{ 
                        fontFamily: "'Cormorant Garamond', serif",
                        fontWeight: 600, 
                        fontStyle: "italic",
                        textTransform: "none", // No uppercase
                        color: "#f4f4f5", // Parchment
                        mb: 0.5,
                        lineHeight: 1.1
                    }}
                  >
                    {title}
                  </Typography>
                )}
                {subtitle && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#a1a1aa",
                      mt: 0.5,
                      maxWidth: 480,
                      fontFamily: "'Geist', sans-serif"
                    }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Box>
              {actions}
            </Box>
            <Box
              sx={{
                width: "100%",
                height: "1px",
                background: "color-mix(in srgb, var(--text-primary) 8%, transparent)", // Neutral Divider
                mt: 1
              }}
            />
          </Box>
        )}
        {children}
      </Box>
    </Box>
  );
};

NeoPanel.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  children: PropTypes.node.isRequired,
  actions: PropTypes.node,
  sx: PropTypes.object,
  variant: PropTypes.oneOf(["elevated", "flat"]),
};

export default NeoPanel;
